import React, { useEffect, useMemo, useState } from 'react'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form'
import { Col, Container, Form, Row } from 'react-bootstrap'
import { Modal } from '../../components/Modal/Modal'
import { FormFeedback } from '../../components/Form/FormFeedback'
import { Button } from '../../components/Button/Button'
import { SelectDropdown } from '../../components/SelectDropdown/SelectDropdown'
import { getInviteRoleGroups } from './InviteRoleGroups'
import { ScopedBlock } from '../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../components/ScopedBlock/models'
import { useGrantCrossAccountRequestToUserMutation } from '../../store/crossAccount/crossAccount'
import { useGetAccountQuery } from '../../store/accountUserSettings/accountUserSettings'

export interface GrantCrossAccountAccessModalProps {
  show: boolean
  onHide: (isSuccess: boolean) => void
}

type FormValues = {
  grantId: string
  roleGroupName: string
}

export const GrantCrossAccountAccessModal: React.FC<GrantCrossAccountAccessModalProps> = ({ show, onHide }) => {
  const accountId = useAppSelector(selectAccountId)
  const { data: account } = useGetAccountQuery({ accountId })
  const [crossAccountAccessIsEnabled, setCrossAccountAccessIsEnabled] = useState(false)
  const [grantAccess, { isLoading: isGrantLoading, isSuccess }] = useGrantCrossAccountRequestToUserMutation()
  const inviteRoleGroups = useMemo(() => getInviteRoleGroups({ onlyCrossAccountGrantable: true }), [])

  useEffect(() => {
    if (account) {
      setCrossAccountAccessIsEnabled(account.data.effectiveConfig.crossAccountAccess)
    }
  }, [account])

  const defaultValues = {
    grantId: '',
    roleGroupName: getInviteRoleGroups({ onlyCrossAccountGrantable: true })[0].name,
  }

  const handleSave = (data: FormValues) => {
    grantAccess({
      accountId,
      grantId: data.grantId,
      roleIds: inviteRoleGroups.find((group) => group.name === data.roleGroupName)?.roleIds || [],
    })
  }

  useEffect(() => {
    if (isSuccess) {
      onHide(isSuccess)
      form.reset()
    }
  }, [isSuccess])

  const form = useForm<FormValues>({ defaultValues })
  const { handleSubmit } = form

  const body = (
    <Container>
      <Row className='pb-3'>
        <Col>
          <h4 className='d-flex justify-content-center align-items-center'>Grant cross-account access</h4>
        </Col>
      </Row>
      <Row className='pb-4'>
        <Col className='p-0'>
          <div className='paragraph-medium'>
            To grant access to someone from another Zocks account, enter their unique token below. They’ll need to accept before they can join your team.
          </div>
        </Col>
      </Row>
      <Row>
        <FormProvider {...form}>
          <GrantCrossAccountAccessForm />
        </FormProvider>
      </Row>
    </Container>
  )

  const footer = (
    <div className='d-flex gap-3'>
      <Button variant='secondary' onClick={() => onHide(false)}>
        Cancel
      </Button>
      <ScopedBlock scope={{ scope: 'consoleuser:invite', superScope: ['consoleuser:admin'] }} missingScopeAction={MissingScopeAction.DISABLE}>
        <Button
          variant='primary'
          onClick={handleSubmit(handleSave)}
          loading={isGrantLoading}
          title={!crossAccountAccessIsEnabled ? 'Cross account access is not enabled' : ''}
          disabled={!crossAccountAccessIsEnabled}
        >
          Grant Access
        </Button>
      </ScopedBlock>
    </div>
  )

  return <Modal show={show} onHide={() => onHide(false)} header={<></>} body={body} footer={footer} centered={true} />
}

const GrantCrossAccountAccessForm: React.FC = () => {
  const { control } = useFormContext<FormValues>()
  const inviteRoleGroups = useMemo(() => getInviteRoleGroups({ onlyCrossAccountGrantable: true }), [])

  return (
    <Container className='d-flex flex-column gap-4 p-0'>
      <Row>
        <Col>
          <Controller
            name='grantId'
            control={control}
            rules={{
              required: 'User ID required.',
              pattern: {
                value: /^[a-z0-9]{16}\.[a-z0-9]{16}$/,
                message: 'Entered value does not match user ID format',
              },
            }}
            render={({ field, fieldState }) => (
              <>
                <Form.Label className='bold'>User ID</Form.Label>
                <Form.Control id={field.name} type='text' placeholder='XXXXXXXXXXXXXXXX.XXXXXXXXXXXXXXXX' {...field} isInvalid={!!fieldState.error} />
                {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
              </>
            )}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <Controller
            name='roleGroupName'
            control={control}
            render={({ field, fieldState }) => (
              <>
                <Form.Label className='bold'>Role</Form.Label>
                <SelectDropdown
                  className='zocks-px-0.75 py-2'
                  options={inviteRoleGroups.map((roleGroup) => {
                    return { id: roleGroup.name, label: roleGroup.name }
                  })}
                  initialSelectedOption={inviteRoleGroups
                    .map((roleGroup) => ({ id: roleGroup.name, label: roleGroup.name }))
                    .find((option) => option.id === field.value)}
                  onOptionSelected={(option) => field.onChange(option.id)}
                />
                {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
              </>
            )}
          />
        </Col>
      </Row>
    </Container>
  )
}
